import { useState, useEffect } from "react";
import { FaTrash, FaEye, FaSearch, FaMoneyBill, FaUser, FaCalendar, FaCheckCircle, FaTimesCircle, FaClock, FaSpinner, FaChevronLeft, FaChevronRight, FaDownload, FaCopy, FaCheck, FaTimes } from "react-icons/fa";
import { utils, writeFile } from "xlsx";
import axios from "axios";

const PurchasedPlans = () => {
  const [purchases, setPurchases] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const purchasesPerPage = 10;

  useEffect(() => {
    fetchPurchasedPlans();
  }, []);

  // Get all purchased plans API with proper error handling
  const fetchPurchasedPlans = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await axios.get("https://api.dreamtoday.in/api/admin/purchased-plans");
      
      console.log("API Response:", response.data); // Debug log
      
      // Check if response exists and has success true
      if (response.data && response.data.success) {
        // Ensure data is an array, default to empty array if not
        setPurchases(Array.isArray(response.data.data) ? response.data.data : []);
        setTotalCount(response.data.total || 0);
      } else {
        setPurchases([]);
        setTotalCount(0);
        setError(response.data?.message || "Failed to fetch purchased plans");
      }
    } catch (error) {
      console.error("Error fetching purchased plans:", error);
      setPurchases([]);
      setTotalCount(0);
      setError(error.response?.data?.message || "Failed to fetch purchased plans");
    } finally {
      setLoading(false);
    }
  };

  // Delete purchased plan API
  const handleDelete = async (purchaseId) => {
    if (window.confirm("Are you sure you want to delete this purchase record?")) {
      try {
        const response = await axios.delete(`https://api.dreamtoday.in/api/admin/purchased-plan/${purchaseId}`);
        
        if (response.data && response.data.success) {
          alert("Purchase record deleted successfully");
          setPurchases(purchases.filter((p) => p._id !== purchaseId));
          setTotalCount(prev => prev - 1);
        } else {
          alert(response.data.message || "Failed to delete purchase record");
        }
      } catch (error) {
        console.error("Error deleting purchase:", error);
        alert(error.response?.data?.message || "Failed to delete purchase record");
      }
    }
  };

  const exportData = (type) => {
    // Safe mapping with null checks
    const exportPurchases = filteredPurchases.map((purchase) => ({
      ID: purchase?._id || "N/A",
      User: purchase?.userId?.name || "N/A",
      Email: purchase?.userId?.email || "N/A",
      Phone: purchase?.userId?.phone || "N/A",
      PlanCoins: purchase?.planId?.coins || 0,
      BonusCoins: purchase?.bonusCoins || 0,
      TotalCoins: (purchase?.coins || 0) + (purchase?.bonusCoins || 0),
      AmountPaid: purchase?.amountPaid || 0,
      Status: purchase?.status || "N/A",
      TransactionId: purchase?.transactionId || "N/A",
      PurchasedAt: purchase?.purchasedAt ? new Date(purchase.purchasedAt).toLocaleString() : "N/A",
      ExpiredAt: purchase?.expiredAt ? new Date(purchase.expiredAt).toLocaleString() : "N/A",
    }));
    
    const ws = utils.json_to_sheet(exportPurchases);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "PurchasedPlans");
    writeFile(wb, `purchased_plans_${new Date().toISOString().split('T')[0]}.${type}`);
  };

  const openPurchaseDetails = (purchase) => {
    setSelectedPurchase(purchase);
    setIsModalOpen(true);
  };

  const copyToClipboard = (text) => {
    if (text) {
      navigator.clipboard.writeText(text);
      setCopiedId(text);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case "success":
      case "completed":
        return "text-green-600 bg-green-50 border border-green-200";
      case "pending":
        return "text-yellow-600 bg-yellow-50 border border-yellow-200";
      case "failed":
        return "text-red-600 bg-red-50 border border-red-200";
      default:
        return "text-gray-600 bg-gray-50 border border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch(status?.toLowerCase()) {
      case "success":
      case "completed":
        return <FaCheckCircle className="text-green-500" />;
      case "pending":
        return <FaClock className="text-yellow-500" />;
      case "failed":
        return <FaTimesCircle className="text-red-500" />;
      default:
        return <FaClock className="text-gray-500" />;
    }
  };

  // Safe filter with null checks
  const filteredPurchases = Array.isArray(purchases) ? purchases.filter((purchase) => {
    if (!purchase) return false;
    
    const searchTerm = search.toLowerCase();
    return (
      purchase.userId?.name?.toLowerCase().includes(searchTerm) ||
      purchase.userId?.email?.toLowerCase().includes(searchTerm) ||
      purchase.userId?.phone?.toLowerCase().includes(searchTerm) ||
      (purchase.transactionId && purchase.transactionId.toLowerCase().includes(searchTerm)) ||
      purchase._id?.toLowerCase().includes(searchTerm)
    );
  }) : [];

  const indexOfLastPurchase = currentPage * purchasesPerPage;
  const indexOfFirstPurchase = indexOfLastPurchase - purchasesPerPage;
  const currentPurchases = filteredPurchases.slice(indexOfFirstPurchase, indexOfLastPurchase);
  const totalPages = Math.ceil(filteredPurchases.length / purchasesPerPage);

  // Safe statistics calculation
  const calculateStats = () => {
    const validPurchases = Array.isArray(purchases) ? purchases.filter(p => p) : [];
    
    const totalAmount = validPurchases.reduce((sum, p) => sum + (p?.amountPaid || 0), 0);
    const totalCoins = validPurchases.reduce((sum, p) => sum + (p?.coins || 0), 0);
    const totalBonus = validPurchases.reduce((sum, p) => sum + (p?.bonusCoins || 0), 0);
    const successCount = validPurchases.filter(p => p?.status?.toLowerCase() === "success").length;
    
    return { totalAmount, totalCoins, totalBonus, successCount };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading purchased plans...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8 bg-white rounded-2xl shadow-xl">
          <div className="text-red-500 mb-4">
            <FaTimesCircle className="h-16 w-16 mx-auto" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={fetchPurchasedPlans}
            className="px-8 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-medium shadow-lg hover:shadow-xl"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Purchased Plans</h1>
        <p className="text-gray-600">View and manage all plan purchases by users</p>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-xl mr-4">
              <FaMoneyBill className="text-blue-600 text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">₹{stats.totalAmount.toLocaleString()}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-xl mr-4">
              <FaCheckCircle className="text-green-600 text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Successful</p>
              <p className="text-2xl font-bold text-gray-900">{stats.successCount}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-xl mr-4">
              <FaMoneyBill className="text-purple-600 text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Coins</p>
              <p className="text-2xl font-bold text-gray-900">{(stats.totalCoins + stats.totalBonus).toLocaleString()}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition">
          <div className="flex items-center">
            <div className="p-3 bg-amber-100 rounded-xl mr-4">
              <FaCalendar className="text-amber-600 text-xl" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Purchases</p>
              <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Export */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
          <div className="relative flex-1">
            <FaSearch className="absolute left-4 top-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by user name, email, phone or transaction ID..."
              className="w-full px-4 py-3 pl-12 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => exportData("csv")}
              className="px-4 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition flex items-center gap-2 font-medium shadow-md hover:shadow-lg"
              disabled={!Array.isArray(purchases) || purchases.length === 0}
            >
              <FaDownload />
              CSV
            </button>
            <button
              onClick={() => exportData("xlsx")}
              className="px-4 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition flex items-center gap-2 font-medium shadow-md hover:shadow-lg"
              disabled={!Array.isArray(purchases) || purchases.length === 0}
            >
              <FaDownload />
              Excel
            </button>
          </div>
        </div>
      </div>

      {/* Purchases Table */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  User Details
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Plan Info
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Payment
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Dates
                </th>
                <th className="py-4 px-6 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentPurchases.length > 0 ? (
                currentPurchases.map((purchase) => (
                  <tr key={purchase?._id || Math.random()} className="hover:bg-blue-50/30 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md">
                          <FaUser className="text-white" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-semibold text-gray-900">
                            {purchase?.userId?.name || "N/A"}
                          </div>
                          <div className="text-xs text-gray-500 flex items-center gap-1">
                            <span>{purchase?.userId?.email || "N/A"}</span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {purchase?.userId?.phone || "N/A"}
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="py-4 px-6">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-500">Coins:</span>
                          <span className="text-sm font-bold text-blue-600">{purchase?.coins || 0}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-500">Bonus:</span>
                          <span className="text-sm font-bold text-green-600">+{purchase?.bonusCoins || 0}</span>
                        </div>
                        <div className="text-xs text-gray-600">
                          Total: <span className="font-semibold">{(purchase?.coins || 0) + (purchase?.bonusCoins || 0)}</span>
                        </div>
                        {purchase?.planId?.validity && (
                          <div className="text-xs text-gray-500">
                            Validity: {purchase.planId.validity} days
                          </div>
                        )}
                      </div>
                    </td>
                    
                    <td className="py-4 px-6">
                      <div className="space-y-1">
                        <div className="text-lg font-bold text-green-600">
                          ₹{purchase?.amountPaid || 0}
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-1 bg-gray-50 p-1 rounded">
                          <span className="font-medium">Tx:</span>
                          <span className="font-mono text-xs">{purchase?.transactionId ? purchase.transactionId.substring(0, 8) + '...' : 'N/A'}</span>
                          {purchase?.transactionId && (
                            <button
                              onClick={() => copyToClipboard(purchase.transactionId)}
                              className="text-gray-400 hover:text-blue-600 transition ml-1"
                              title="Copy Transaction ID"
                            >
                              {copiedId === purchase.transactionId ? <FaCheck className="text-green-500" size={10} /> : <FaCopy size={10} />}
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                    
                    <td className="py-4 px-6">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${getStatusColor(purchase?.status)}`}>
                        {getStatusIcon(purchase?.status)}
                        <span className="ml-1 capitalize">{purchase?.status || "N/A"}</span>
                      </span>
                    </td>
                    
                    <td className="py-4 px-6">
                      <div className="text-sm">
                        <div className="flex items-center gap-1 text-gray-600">
                          <FaCalendar className="text-gray-400" size={12} />
                          <span className="text-xs">Buy: {purchase?.purchasedAt ? new Date(purchase.purchasedAt).toLocaleDateString() : "N/A"}</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-600 mt-1">
                          <FaClock className="text-gray-400" size={12} />
                          <span className="text-xs">Exp: {purchase?.expiredAt ? new Date(purchase.expiredAt).toLocaleDateString() : "N/A"}</span>
                        </div>
                      </div>
                    </td>
                    
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openPurchaseDetails(purchase)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition border border-blue-200 hover:border-blue-300"
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => handleDelete(purchase?._id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition border border-red-200 hover:border-red-300"
                          title="Delete Record"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="py-16 text-center">
                    <div className="text-gray-300 mb-4">
                      <FaMoneyBill className="h-20 w-20 mx-auto" />
                    </div>
                    <p className="text-gray-500 text-xl font-medium mb-2">No purchases found</p>
                    <p className="text-gray-400">
                      {search ? "Try adjusting your search criteria" : "No plan purchases yet"}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {filteredPurchases.length > 0 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white rounded-xl shadow-lg p-4">
          <div className="text-sm text-gray-600">
            Showing <span className="font-semibold text-blue-600">{indexOfFirstPurchase + 1}</span> to{" "}
            <span className="font-semibold text-blue-600">{Math.min(indexOfLastPurchase, filteredPurchases.length)}</span> of{" "}
            <span className="font-semibold text-blue-600">{filteredPurchases.length}</span> purchases
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
            >
              <FaChevronLeft size={12} /> Previous
            </button>
            
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`w-10 h-10 rounded-lg transition font-medium ${
                      currentPage === pageNumber
                        ? "bg-blue-600 text-white shadow-md"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
            >
              Next <FaChevronRight size={12} />
            </button>
          </div>
        </div>
      )}

      {/* Purchase Details Modal */}
      {isModalOpen && selectedPurchase && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" onClick={() => setIsModalOpen(false)}></div>
          
          {/* Modal Container */}
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Sticky Header */}
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white z-10 p-6 rounded-t-2xl">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold">Purchase Details</h2>
                    <p className="text-blue-100">Complete information about this plan purchase</p>
                  </div>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <FaTimes size={20} />
                  </button>
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="p-6">
                <div className="space-y-6">
                  {/* User Information */}
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-5 border border-blue-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                      <div className="p-2 bg-blue-500 rounded-lg mr-3">
                        <FaUser className="text-white" />
                      </div>
                      User Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white p-4 rounded-xl shadow-sm">
                        <p className="text-sm text-gray-500">Name</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {selectedPurchase?.userId?.name || "N/A"}
                        </p>
                      </div>
                      <div className="bg-white p-4 rounded-xl shadow-sm">
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="text-gray-900 break-all">{selectedPurchase?.userId?.email || "N/A"}</p>
                      </div>
                      <div className="bg-white p-4 rounded-xl shadow-sm">
                        <p className="text-sm text-gray-500">Phone</p>
                        <p className="text-gray-900">{selectedPurchase?.userId?.phone || "N/A"}</p>
                      </div>
                    </div>
                  </div>

                  {/* Plan Information */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border border-green-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                      <div className="p-2 bg-green-500 rounded-lg mr-3">
                        <FaMoneyBill className="text-white" />
                      </div>
                      Plan Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-white p-4 rounded-xl shadow-sm">
                        <p className="text-sm text-gray-500">Base Coins</p>
                        <p className="text-2xl font-bold text-blue-600">{selectedPurchase?.coins || 0}</p>
                      </div>
                      <div className="bg-white p-4 rounded-xl shadow-sm">
                        <p className="text-sm text-gray-500">Bonus Coins</p>
                        <p className="text-2xl font-bold text-green-600">{selectedPurchase?.bonusCoins || 0}</p>
                      </div>
                      <div className="bg-white p-4 rounded-xl shadow-sm">
                        <p className="text-sm text-gray-500">Total Coins</p>
                        <p className="text-2xl font-bold text-purple-600">
                          {(selectedPurchase?.coins || 0) + (selectedPurchase?.bonusCoins || 0)}
                        </p>
                      </div>
                      <div className="bg-white p-4 rounded-xl shadow-sm">
                        <p className="text-sm text-gray-500">Amount Paid</p>
                        <p className="text-2xl font-bold text-amber-600">₹{selectedPurchase?.amountPaid || 0}</p>
                      </div>
                    </div>
                    
                    {selectedPurchase?.planId && (
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white p-3 rounded-xl shadow-sm">
                          <p className="text-xs text-gray-500">Plan Price</p>
                          <p className="font-semibold text-lg">₹{selectedPurchase.planId.price}</p>
                        </div>
                        <div className="bg-white p-3 rounded-xl shadow-sm">
                          <p className="text-xs text-gray-500">Plan Coins</p>
                          <p className="font-semibold text-lg">{selectedPurchase.planId.coins}</p>
                        </div>
                        <div className="bg-white p-3 rounded-xl shadow-sm">
                          <p className="text-xs text-gray-500">Validity</p>
                          <p className="font-semibold text-lg">{selectedPurchase.planId.validity} days</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Transaction Information */}
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-5 border border-purple-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                      <div className="p-2 bg-purple-500 rounded-lg mr-3">
                        <FaCheckCircle className="text-white" />
                      </div>
                      Transaction Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white p-4 rounded-xl shadow-sm">
                        <p className="text-sm text-gray-500">Transaction ID</p>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="font-mono text-gray-900 break-all bg-gray-50 p-2 rounded">{selectedPurchase?.transactionId || "N/A"}</p>
                          {selectedPurchase?.transactionId && (
                            <button
                              onClick={() => copyToClipboard(selectedPurchase.transactionId)}
                              className="p-2 text-gray-400 hover:text-blue-600 transition bg-gray-50 rounded-lg"
                              title="Copy Transaction ID"
                            >
                              {copiedId === selectedPurchase.transactionId ? <FaCheck className="text-green-500" /> : <FaCopy />}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="bg-white p-4 rounded-xl shadow-sm">
                        <p className="text-sm text-gray-500">Status</p>
                        <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold mt-1 ${getStatusColor(selectedPurchase?.status)}`}>
                          {getStatusIcon(selectedPurchase?.status)}
                          <span className="ml-2 capitalize">{selectedPurchase?.status || "N/A"}</span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Dates Information */}
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-5 border border-amber-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                      <div className="p-2 bg-amber-500 rounded-lg mr-3">
                        <FaCalendar className="text-white" />
                      </div>
                      Timeline
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white p-4 rounded-xl shadow-sm">
                        <p className="text-sm text-gray-500">Purchased At</p>
                        <p className="font-medium text-gray-900">
                          {selectedPurchase?.purchasedAt ? new Date(selectedPurchase.purchasedAt).toLocaleString() : "N/A"}
                        </p>
                      </div>
                      <div className="bg-white p-4 rounded-xl shadow-sm">
                        <p className="text-sm text-gray-500">Expires At</p>
                        <p className="font-medium text-gray-900">
                          {selectedPurchase?.expiredAt ? new Date(selectedPurchase.expiredAt).toLocaleString() : "N/A"}
                        </p>
                      </div>
                      {selectedPurchase?.purchasedAt && selectedPurchase?.expiredAt && (
                        <div className="bg-white p-4 rounded-xl shadow-sm">
                          <p className="text-sm text-gray-500">Time Remaining</p>
                          <p className="font-medium text-gray-900">
                            {(() => {
                              const now = new Date();
                              const expiry = new Date(selectedPurchase.expiredAt);
                              const diff = expiry - now;
                              if (diff < 0) return <span className="text-red-600">Expired</span>;
                              const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                              const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                              return <span className="text-green-600">{days}d {hours}h remaining</span>;
                            })()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Sticky Footer */}
              <div className="sticky bottom-0 bg-gray-50 border-t p-6">
                <div className="flex gap-3">
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="flex-1 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition font-medium shadow-lg"
                  >
                    Close Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchasedPlans;